import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import { type PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import type {
  IParamsGetPowerDialerContacts,
  IResponsePowerDialerContactsList,
  PowerDialerContactStatus,
} from 'entities/PowerDialer/api/types'
import { Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'

export class PowerDialerContactsStore {
  private _powerDialer: PowerDialer | null = null
  private _selectedFilterKey: PowerDialerContactStatus = 'enrolled'
  private _contactsMap: Map<number, Contact> = new Map()
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private readonly _tableStore: TableStore<RowContact>
  private readonly _contactsTagsModalStore: ContactsTagsModalStore

  private _search = ''
  scrollToTopTrigger = ''

  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore<RowContact>({
      key: 'power_dialer_contacts',
      element: 'contact',
      useColumnsCache: true,
      limit: 10,
      page: 1,
    })
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._setupGetItemsReaction()
    this._setupParamsWithoutPageReaction()
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get tableStore() {
    return this._tableStore
  }

  init = async (powerDialer: PowerDialer) => {
    this._powerDialer = powerDialer
    this._loadContactByParams()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      if (!this._powerDialer) return

      const contactsResponse = await PowerDialerApi.getPowerDialerContacts(
        this._powerDialer.id,
        this.paramsGetItems,
        {
          cancelToken: cancelRequest.token,
        }
      )
      if (!contactsResponse) return
      if (this.paramsGetItems.search && this._entities.organizationStore.isActivityLoggingEnabled) {
        this._entities.organizationStore.handleTrackOrganizationActivity({
          event: 'phi.contact.searched',
          data: {
            search_query: this.paramsGetItems.search,
          },
        })
      }
      this._setContacts(contactsResponse.data)
    },
  })

  private _loadContactByParams = async () => {
    return this._requestLoadData.loadData()
  }

  private _setContacts({ data, meta }: IResponsePowerDialerContactsList) {
    this._contactsMap.clear()
    data.forEach((item) => {
      this._contactsMap.set(item.id, new Contact(item))
    })
    this._tableStore.setTotal(meta.total)
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setFilter = (filter: ICardFilter<typeof this._selectedFilterKey>) => {
    this._selectedFilterKey = filter.key
  }

  onSuccessDelete = () => {
    this.tableStore.unselectAllIds()
    void this._loadContactByParams()
  }

  updatePowerDialerData = (updatedPowerDialer: PowerDialer) => {
    this._powerDialer = updatedPowerDialer
    this.reset()
    void this._loadContactByParams()
  }

  private _setupGetItemsReaction = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _setupParamsWithoutPageReaction = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._requestLoadData.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  get filters(): Array<ICardFilter<typeof this._selectedFilterKey>> {
    return [
      {
        key: 'enrolled',
        active: this._selectedFilterKey === 'enrolled',
        amount: this.statistics?.enrolled,
        percentage: this.statistics?.enrolledPercent,
        title: 'Enrolled',
      },
      {
        key: 'contacted',
        active: this._selectedFilterKey === 'contacted',
        amount: this.statistics?.contacted,
        percentage: this.statistics?.contactedPercent,
        title: 'Contacted',
      },
      {
        key: 'not_contacted',
        active: this._selectedFilterKey === 'not_contacted',
        amount: this.statistics?.noContacted,
        percentage: this.statistics?.notContactedPercent,
        title: 'Not contacted',
      },
      {
        key: 'answered',
        active: this._selectedFilterKey === 'answered',
        amount: this.statistics?.answered,
        percentage: this.statistics?.answeredPercent,
        title: 'Answered',
      },
      {
        key: 'no_answered',
        active: this._selectedFilterKey === 'no_answered',
        amount: this.statistics?.noAnswered,
        percentage: this.statistics?.noAnsweredPercent,
        title: 'No answer',
      },
      {
        key: 'skipped',
        active: this._selectedFilterKey === 'skipped',
        amount: this.statistics?.skipped,
        percentage: this.statistics?.skippedPercent,
        title: 'Skipped by user',
      },
    ]
  }

  get selectedFilterKey() {
    return this._selectedFilterKey
  }

  get contacts() {
    return Array.from(this._contactsMap.values())
  }

  get isNoData() {
    return !this.isLoading && !this._contactsMap.size && !this._search
  }

  get paramsWithoutPage(): Omit<IParamsGetPowerDialerContacts, 'page'> {
    return {
      limit: this._tableStore.limit,
      status: this._selectedFilterKey || undefined,
      search: this._search,
    }
  }

  get paramsGetItems(): IParamsGetPowerDialerContacts {
    return {
      ...this.paramsWithoutPage,
      page: this._tableStore.page,
    }
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: async (data) => {
        this._contactsDetailsManageStore.setDisabled(true)
        try {
          await this._entities.inboxesStore.fetchInboxes()
          const sharedInboxes = this._entities.inboxesStore.sharedInboxes

          const inboxIdFromModel = this._powerDialer?.inbox.id
          const inboxId = sharedInboxes.find((inbox) => inbox.id === inboxIdFromModel)?.id

          if (!inboxId) {
            modalStore.addModal({
              disabledOnAllClose: true,
              type: ModalTypeList.INFO,
              title: 'Content unavailable',
              id: 'content_unavailable',
              desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
            })

            return
          }

          await this._contactsDetailsManageStore.onOpenContactDetails(
            {
              contactId: data.contactId,
              inboxId,
            },
            'pdCampaign',
            false
          )
        } finally {
          this._contactsDetailsManageStore.setDisabled(false)
        }
      },
    }
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get statistics() {
    return this._powerDialer?.statistics ?? null
  }

  get isLoading() {
    return this._requestLoadData.loading
  }

  get search() {
    return this._search
  }

  reset = () => {
    this._tableStore.unselectAllIds()
    this._tableStore.dispose()
    this._selectedFilterKey = 'enrolled'
    this._search = ''
    this._contactsMap.clear()
  }

  dispose = () => {
    this._requestLoadData.dispose()
    this._disposeParamsGetItems?.()
    this._disposeParamsWithoutPage?.()
  }
}
